import { cloneDeepWith, isString } from 'lodash'

/**
 * Checks if a string is a valid URL or a React Router path with query parameters.
 * Handles:
 * - Absolute URLs: https://example.com/path?query
 * - React Router paths: /path?query (paths starting with /)
 */
const isValidUrl = (str: string): boolean => {
  try {
    new URL(str)
    return true
  } catch {
    if (str.startsWith('/') && str.includes('?')) {
      return true
    }
    return false
  }
}

/**
 * Sanitizes a URL by removing all query parameters.
 * Handles:
 * - Absolute URLs: https://example.com/path?query → https://example.com/path
 * - React Router paths: /path?query → /path
 *
 * @param url - The URL to sanitize
 * @returns Sanitized URL without query parameters
 *
 * @example
 * ```ts
 * sanitizeUrl('https://example.com/path?e=token&c=code')
 * // Returns: 'https://example.com/path'
 *
 * sanitizeUrl('/employees/onboarding/welcome?e=token&c=code')
 * // Returns: '/employees/onboarding/welcome'
 * ```
 */
export const sanitizeUrl = (url?: string): string | undefined => {
  if (!url) return url

  if (!url.includes('?')) return url

  if (url.startsWith('/')) {
    const [beforeQuery, afterQuery] = url.split('?')
    const hashIndex = afterQuery?.indexOf('#')
    const hash =
      hashIndex !== undefined && hashIndex !== -1 ? '#' + afterQuery.substring(hashIndex + 1) : ''
    return beforeQuery + hash
  }

  try {
    const u = new URL(url)
    u.search = ''
    return u.toString()
  } catch {
    const queryIndex = url.indexOf('?')
    const hashIndex = url.indexOf('#')
    if (hashIndex !== -1 && hashIndex > queryIndex) {
      return url.substring(0, queryIndex) + url.substring(hashIndex)
    }
    return url.substring(0, queryIndex)
  }
}

/**
 * Sanitizes an object by recursively removing all query parameters from URL strings.
 *
 * @param obj - The object to sanitize
 * @returns Sanitized object with URLs cleaned
 *
 * @example
 * ```ts
 * sanitizeObject({
 *   url: 'https://example.com?e=token',
 *   data: { redirect: 'https://example.com?c=code' }
 * })
 * // Returns: { url: 'https://example.com', data: { redirect: 'https://example.com' } }
 * ```
 */
export const sanitizeObject = (obj: any) =>
  cloneDeepWith(obj, (value) => {
    if (isString(value)) return isValidUrl(value) ? sanitizeUrl(value) : value
  })

/**
 * Sanitizes Sentry tags safely.
 *
 * @param tags - Sentry tags object
 * @returns Sanitized tags object with string values only
 */
export const sanitizeTags = (
  tags: Record<string, any> | undefined
): Record<string, string> | undefined => {
  if (!tags) return tags

  const sanitized: Record<string, string> = {}
  for (const [key, value] of Object.entries(tags)) {
    if (value === null || value === undefined) {
      continue
    }
    const stringValue = String(value)
    sanitized[key] = isValidUrl(stringValue) ? sanitizeUrl(stringValue) ?? stringValue : stringValue
  }
  return sanitized
}

/**
 * Sanitizes breadcrumb data, handling common URL fields (url, to, from).
 */
export const sanitizeBreadcrumbData = (data: any): any => {
  if (!data || typeof data !== 'object') return data

  const sanitized = { ...data }
  if (typeof sanitized.url === 'string') {
    sanitized.url = sanitizeUrl(sanitized.url)
  }
  if (typeof sanitized.to === 'string') {
    sanitized.to = sanitizeUrl(sanitized.to)
  }
  if (typeof sanitized.from === 'string') {
    sanitized.from = sanitizeUrl(sanitized.from)
  }
  return sanitized
}
